import React, { memo, useEffect, useState } from "react";
import { PolicyFullObject } from "../../types/insurance.ts";
import SelectGroup from "./SelectGroup/SelectGroup.tsx";
import SectionHeader from "../SectionHeader/SectionHeader.tsx";
import { inputClass, labelClass } from "../../constants/formStyles";

interface VehicleFormProps {
  policyData: PolicyFullObject;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  saveVehicleData: () => void;
  vehicleTypes: { [key: string]: string };
  vehicleUsages: { [key: string]: string };
  loadCarDataById: (regNumber?: string, certificate?: string) => void;
}

interface LocalVehicleState {
  vehicleRegistrationNumber: string;
  vehicleCertificate: string;
  vehicleType: string;
  vehicleUsage: string;
  vehicleBrand: string;
  vehicleModel: string;
  engineCapacity: string;
  vehicleVolume: string;
  manufactureYear: string;
  chassisNumber: string;
}

const fromPolicy = (policyData: PolicyFullObject): LocalVehicleState => ({
  vehicleRegistrationNumber: policyData.vehicleRegistrationNumber || "",
  vehicleCertificate: policyData.vehicleCertificate || "",
  vehicleType: policyData.vehicleType || "",
  vehicleUsage: policyData.vehicleUsage || "",
  vehicleBrand: policyData.vehicleBrand || "",
  vehicleModel: policyData.vehicleModel || "",
  engineCapacity: policyData.engineCapacity || "",
  vehicleVolume: policyData.vehicleVolume || "",
  manufactureYear: policyData.manufactureYear || "",
  chassisNumber: policyData.chassisNumber || "",
});

const VehicleFormComponent: React.FC<VehicleFormProps> = ({
  policyData,
  handleChange,
  saveVehicleData,
  loadCarDataById,
  vehicleTypes,
  vehicleUsages,
}) => {
  const [local, setLocal] = useState<LocalVehicleState>(() =>
    fromPolicy(policyData)
  );

  // Sync on policy change AND when loadCarDataById populates fields from external lookup.
  // vehicleBrand + chassisNumber are reliable indicators that a car lookup completed.
  useEffect(() => {
    setLocal(fromPolicy(policyData));
  }, [
    policyData.policyId,
    policyData.vehicleBrand,
    policyData.vehicleModel,
    policyData.chassisNumber,
    policyData.vehicleCertificate,
    policyData.engineCapacity,
    policyData.vehicleVolume,
    policyData.manufactureYear,
  ]);

  const handleLocalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLocal((prev) => ({ ...prev, [name]: value }));
  };

  const handleBlur = (field: keyof LocalVehicleState) => () => {
    handleChange({
      target: { name: field, value: local[field] },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const handleRegNumberBlur = () => {
    handleChange({
      target: {
        name: "vehicleRegistrationNumber",
        value: local.vehicleRegistrationNumber,
      },
    } as React.ChangeEvent<HTMLInputElement>);

    loadCarDataById(local.vehicleRegistrationNumber, local.vehicleCertificate);
  };

  return (
    <fieldset className="border-stroke dark:border-strokedark shadow-default dark:bg-boxdark rounded-sm border bg-white">
      <SectionHeader title="Данни за МПС" />

      <div className="p-3 pt-1">
        {/* Row 1 — 5 fields */}
        <div className="mb-2 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
          <div>
            <label className={labelClass}>Рег. номер</label>
            <input
              type="text"
              name="vehicleRegistrationNumber"
              value={local.vehicleRegistrationNumber}
              onChange={handleLocalChange}
              onBlur={handleRegNumberBlur}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>№ Талон</label>
            <input
              type="text"
              name="vehicleCertificate"
              value={local.vehicleCertificate}
              onChange={handleLocalChange}
              onBlur={handleBlur("vehicleCertificate")}
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>№ Рама</label>
            <input
              name="chassisNumber"
              value={local.chassisNumber}
              onChange={handleLocalChange}
              onBlur={handleBlur("chassisNumber")}
              type="text"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Вид МПС</label>
            <SelectGroup
              name="vehicleType"
              options={vehicleTypes}
              value={local.vehicleType || 1}
              onChange={(selectedValue: string) => {
                setLocal((prev) => ({ ...prev, vehicleType: selectedValue }));
                handleChange({
                  target: { name: "vehicleType", value: selectedValue },
                } as React.ChangeEvent<HTMLInputElement>);
              }}
            />
          </div>

          <div>
            <label className={labelClass}>Марка</label>
            <input
              type="text"
              name="vehicleBrand"
              value={local.vehicleBrand}
              onChange={handleLocalChange}
              onBlur={handleBlur("vehicleBrand")}
              className={inputClass}
            />
          </div>
        </div>

        {/* Row 2 — 5 fields */}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
          <div>
            <label className={labelClass}>Модел</label>
            <input
              name="vehicleModel"
              value={local.vehicleModel}
              type="text"
              onChange={handleLocalChange}
              onBlur={handleBlur("vehicleModel")}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>ДВГ Кубатура</label>
            <input
              name="engineCapacity"
              value={local.engineCapacity}
              onChange={handleLocalChange}
              onBlur={handleBlur("engineCapacity")}
              type="text"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>ДВГ Мощност</label>
            <input
              name="vehicleVolume"
              value={local.vehicleVolume}
              onChange={handleLocalChange}
              onBlur={handleBlur("vehicleVolume")}
              type="text"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Година произв.</label>
            <input
              name="manufactureYear"
              type="text"
              onChange={handleLocalChange}
              onBlur={handleBlur("manufactureYear")}
              value={local.manufactureYear}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Ползване</label>
            <SelectGroup
              name="vehicleUsage"
              options={vehicleUsages}
              value={local.vehicleUsage || 1}
              onChange={(selectedValue: string) => {
                setLocal((prev) => ({ ...prev, vehicleUsage: selectedValue }));
                handleChange({
                  target: { name: "vehicleUsage", value: selectedValue },
                } as React.ChangeEvent<HTMLInputElement>);
              }}
            />
          </div>
        </div>

        <div className="mt-3 flex justify-center gap-3">
          <button
            onClick={saveVehicleData}
            className="bg-primary mx-auto mt-4 flex w-1/3 justify-center rounded px-3 py-1.5 text-xs font-medium text-white hover:bg-opacity-90 sm:w-1/6"
          >
            Запис на данни
          </button>
        </div>
      </div>
    </fieldset>
  );
};

VehicleFormComponent.displayName = "VehicleForm";
const VehicleForm = memo(VehicleFormComponent);
export default VehicleForm;
